import {useReducer, useEffect, useMemo, FC, useState, useRef} from "react";
import {
    Box,
    Button,
    Divider,
    Dialog,
    CircularProgress,
    DialogActions,
} from "@mui/material";
import {EmptyFilterComponent} from "./buildables/EmptyFilterComponent";
import {FilterListOptions} from "./buildables/FilterListOptions";
import {FilterListSelection} from "./buildables/FilterListSelection";
import {TreeViewFilterListSelection} from "./buildables/TreeViewFilterListSelection";
import {FilterListDisplay} from "./buildables/FilterListDisplay";
import {DateRange} from "@mui/x-date-pickers-pro";
import {DateTime} from "luxon";
import {startCase} from "lodash";
import {
    filterReducer,
    FilterOption,
    IncludeExcludeFilterCategory,
    areCategoriesStructurallyEqual,
} from "./reducers/includeExcludeFilterReducer";

export interface IncludeExcludeFilterType {
    include: Record<string, string[] | DateRange<DateTime>>;
    exclude: Record<string, string[] | DateRange<DateTime>>;
}

interface FilterComponentProps {
    categories: IncludeExcludeFilterCategory[];
    customLabels?: Record<string, string>;
    onApplyFilters: (selectedFilters: IncludeExcludeFilterType) => void;
    onCancel: () => void;
    openFilter?: boolean;
    onCloseFilter?: () => void;
    isLoading?: boolean;
    existingFilters?: IncludeExcludeFilterType;
    handleServerSearch?: (categoryId: string, search: string) => void;
    serverSearchOptions?: FilterOption[];
    serverSearchLoading?: boolean;
    serverSearchMinChars?: number;
    serverSearchCategoryId?: string | null;
    sortItems?: boolean;
    useTreeViewSelection?: boolean;
    filterDialogMode?: "legacy" | "browse-all";
    chipDisplayMode?: "legacy" | "consolidated";
    onCategoryClear?: (categoryId: string, filterType: "include" | "exclude") => void;
    onClearAll?: () => void;
    onModeSwitch?: (filterType: "include" | "exclude") => void;
}

export const INCLUDE_EXCLUDE_BORDER = "1px solid #e0e0e0";
export const INCLUDE_EXCLUDE_HEIGHT_OF_HEADER = "54px";

const formatCamelCaseToWords = (camelCase: string): string => {
    if (!camelCase) return "";
    return startCase(camelCase);
};

const IncludeExcludeFilter: FC<FilterComponentProps> = ({
                                                            categories = [],
                                                            customLabels = {},
                                                            onApplyFilters,
                                                            onCancel,
                                                            openFilter = false,
                                                            onCloseFilter,
                                                            isLoading = false,
                                                            existingFilters,
                                                            handleServerSearch,
                                                            serverSearchOptions,
                                                            serverSearchLoading,
                                                            serverSearchMinChars,
                                                            serverSearchCategoryId,
                                                            sortItems = true,
                                                                                                                    useTreeViewSelection = false,
                                                                                                                    filterDialogMode = "legacy",
                                                                                                                    chipDisplayMode = "legacy",
                                                                                                                    onCategoryClear,
                                                                                                                    onClearAll,
                                                                                                                    onModeSwitch,
                                                        }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [resetSearchSignal, setResetSearchSignal] = useState(0);
    const previousCategoriesRef = useRef<IncludeExcludeFilterCategory[]>([]);
    const hasInitializedRef = useRef(false);

    const validCategories = useMemo(() => {
        if (!isLoading) {
            let cats = categories
                .filter(
                    (cat) =>
                        cat &&
                        typeof cat === "object" &&
                        typeof cat.id === "string" &&
                        typeof cat.label === "string" &&
                        Array.isArray(cat.options),
                )
                .map((cat) => ({
                    ...cat,
                    options: cat.options.filter((opt) => opt && typeof opt === "object"),
                }));
            if (sortItems) {
                cats = cats.sort((a, b) => a.label.localeCompare(b.label));
            }
            return cats;
        }
        return [];
    }, [categories, isLoading, sortItems]);

    const [filterSelectionState, dispatch] = useReducer(filterReducer, {
        options: {include: [], exclude: []},
        filters: {include: {}, exclude: {}},
        isInitializing: true,
        chipDisplayMode,
    });

    // Initialize when dialog opens
    useEffect(() => {
        // Reset the init flag when the dialog closes so re-opening triggers a fresh init
        if (!openFilter) {
            hasInitializedRef.current = false;
            return;
        }

        if (hasInitializedRef.current || isLoading) return;

        if (validCategories.length > 0) {
            dispatch({
                type: "INIT",
                payload: {
                    categories: validCategories,
                    existingFilters,
                },
            });
            hasInitializedRef.current = true;
            previousCategoriesRef.current = validCategories;
            if (!selectedCategory) setSelectedCategory(validCategories[0].id);
        } else {
            // No filter categories available (e.g. data fetch failed) — stop initializing
            dispatch({
                type: "INIT",
                payload: {categories: [], existingFilters: existingFilters ?? {include: {}, exclude: {}}},
            });
            hasInitializedRef.current = true;
        }
    }, [
        openFilter,
        isLoading,
        validCategories,
        existingFilters,
        selectedCategory,
    ]);

    // Update options when categories change (but preserve user selections)
    useEffect(() => {
        if (
            hasInitializedRef.current &&
            validCategories.length > 0 &&
            !areCategoriesStructurallyEqual(previousCategoriesRef.current, validCategories)
        ) {
            dispatch({
                type: "UPDATE_OPTIONS",
                payload: {categories: validCategories},
            });
            previousCategoriesRef.current = validCategories;

            // If no category is selected yet (e.g. init happened with empty categories), default to the first available category.
            if (!selectedCategory) {
                setSelectedCategory(validCategories[0].id);
            }
        }
    }, [validCategories, selectedCategory]);

    useEffect(() => {
        if (
            serverSearchOptions &&
            serverSearchCategoryId &&
            serverSearchOptions.length > 0
        ) {
            // Update reducer with new server options for the correct tab (include/exclude)
            dispatch({
                type: "SET_SERVER_OPTIONS",
                payload: {
                    filterType: activeTab === 0 ? "include" : "exclude",
                    categoryId: serverSearchCategoryId,
                    options: serverSearchOptions,
                },
            });
        }
    }, [serverSearchOptions, serverSearchCategoryId, activeTab]);

    const calculateTotalSelectedFilters = (): number => {
        const filters = filterSelectionState.filters;
        return (
            Object.values(filters.include).flat().length +
            Object.values(filters.exclude).flat().length
        );
    };
    const totalSelectedFilters = calculateTotalSelectedFilters();

    const getCategoryLabel = (categoryId: string): string => {
        const customLabel = customLabels[categoryId];
        if (customLabel) {
            return customLabel;
        }

        const findCategoryLabel = (options: FilterOption[]): string | undefined => {
            for (const opt of options) {
                if (opt.categoryId === categoryId) {
                    return opt.label;
                }
                if (opt.subItems && opt.subItems.length > 0) {
                    const label = findCategoryLabel(opt.subItems);
                    if (label) return label;
                }
            }
            return undefined;
        };

        for (const category of validCategories) {
            if (category.id === categoryId) {
                return category.label;
            }
            const label = findCategoryLabel(category.options);
            if (label) return label;
        }

        return formatCamelCaseToWords(categoryId); // Fallback if no match is found
    };

    const handleApply = () => {
        onApplyFilters({
            include: filterSelectionState.filters.include,
            exclude: filterSelectionState.filters.exclude,
        });
        setResetSearchSignal((s) => s + 1);
    };
    const handleCancel = () => {
        onCancel();
        dispatch({
            type: "INIT",
            payload: {
                categories: validCategories,
                existingFilters: existingFilters,
            },
        });
        setResetSearchSignal((s) => s + 1);
    };
    const handleCategoryChange = (categoryId: string) => {
        setSelectedCategory(categoryId);
        setResetSearchSignal((s) => s + 1);
    };

    if (openFilter && (isLoading || filterSelectionState.isInitializing)) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                width="100%"
                position="absolute"
                top={0}
                left={0}
                zIndex={1000}
                bgcolor="background.default"
                sx={{
                    backdropFilter: "blur(4px)",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
            >
                <CircularProgress/>
            </Box>
        );
    }

    if (validCategories.length === 0) {
        return (
            <EmptyFilterComponent
                openFilter={openFilter}
                onCloseFilter={onCloseFilter!}
                onCancel={onCancel}
            />
        );
    }

    // Tree-view rendering is only needed for categories whose options are actually
    // nested (e.g. Business Unit: Segment -> Division -> Region -> District). Flat
    // categories (e.g. Event Type) should render identically everywhere, regardless
    // of whether the caller opted into tree-view support for the dialog as a whole.
    const selectedCategoryDefinition = validCategories.find(
        (cat) => cat.id === selectedCategory,
    );
    const isHierarchicalCategory = !!selectedCategoryDefinition?.options.some(
        (opt) => Boolean(opt.subItems?.length),
    );

    const SelectionComponent = useTreeViewSelection && isHierarchicalCategory
        ? TreeViewFilterListSelection
        : FilterListSelection;

    return (
        <Dialog
            open={openFilter}
            keepMounted
            maxWidth="md"
            fullWidth
            onClose={onCloseFilter}
            aria-labelledby="mega-filter-title"
            aria-describedby="filter-description"
        >
            <Box display="flex" height="600px" maxHeight="600px">
                <FilterListOptions
                    validCategories={validCategories}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={handleCategoryChange}
                    filterSelectionState={filterSelectionState}
                    showSelectionCounts={filterDialogMode === "browse-all"}
                />
                <SelectionComponent
                    validCategories={validCategories}
                    selectedCategory={selectedCategory}
                    filteredOptions={filterSelectionState.options}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    filterSelectionState={filterSelectionState}
                    dispatch={dispatch}
                    handleServerSearch={handleServerSearch}
                    resetSearchSignal={resetSearchSignal}
                    serverSearchLoading={serverSearchLoading}
                    serverSearchMinChars={serverSearchMinChars}
                    onModeSwitch={onModeSwitch}
                />
                <FilterListDisplay
                    getCategoryLabel={getCategoryLabel}
                    totalSelectedFilters={totalSelectedFilters}
                    validCategories={validCategories}
                    filterSelectionState={filterSelectionState}
                    dispatch={dispatch}
                    onClearAll={onClearAll}
                    onCategoryClear={onCategoryClear}
                />
            </Box>
            <Divider/>
            <DialogActions>
                <Box display="flex" justifyContent="space-between" width="100%">
                    <Button
                        variant="text"
                        sx={{color: "text.secondary"}}
                        onClick={handleCancel}
                    >
                        CANCEL
                    </Button>
                    <Button variant="text" color="primary" onClick={handleApply}>
                        APPLY
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default IncludeExcludeFilter;
